import React from 'react';

/*
 * Decorative animated backdrop for the Login / Signup screens.
 * Shopping-themed emojis drift upward while soft brand-coloured
 * blobs float behind the card. Purely cosmetic: sits behind the
 * form (z-index 0), ignores pointer events, and freezes when the
 * user prefers reduced motion.
 */

/* Bag and diamond glyphs dropped here too, to match the app-wide backdrop */
const ICONS = [
  { char: '🛒', left: '6%', size: '2.4rem', delay: '0s', duration: '17s' },
  { char: '🎁', left: '30%', size: '2.1rem', delay: '7s', duration: '19s' },
  { char: '💳', left: '43%', size: '1.7rem', delay: '1.5s', duration: '23s' },
  { char: '📦', left: '55%', size: '2.3rem', delay: '9s', duration: '18s' },
  { char: '🏷️', left: '66%', size: '1.6rem', delay: '5s', duration: '22s' },
  { char: '⭐', left: '77%', size: '1.5rem', delay: '11s', duration: '20s' },
  { char: '👟', left: '86%', size: '2rem', delay: '2.5s', duration: '24s' },
  { char: '⌚', left: '50%', size: '1.6rem', delay: '15s', duration: '26s' },
  { char: '🎧', left: '72%', size: '1.9rem', delay: '8s', duration: '19s' }
];

function AuthBackground() {
  return (
    <div className="auth-bg" aria-hidden="true">
      <span className="auth-blob auth-blob-1" />
      <span className="auth-blob auth-blob-2" />
      <span className="auth-blob auth-blob-3" />

      {ICONS.map((icon, i) => (
        <span
          key={i}
          className="auth-float"
          style={{
            left: icon.left,
            fontSize: icon.size,
            animationDelay: icon.delay,
            animationDuration: icon.duration
          }}
        >
          {icon.char}
        </span>
      ))}

      <style>{authBgStyles}</style>
    </div>
  );
}

const authBgStyles = `
  .auth-bg {
    position: absolute;
    inset: 0;
    overflow: hidden;
    z-index: 0;
    pointer-events: none;
  }

  /* Soft drifting brand-colour blobs */
  .auth-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.55;
    will-change: transform;
  }
  .auth-blob-1 {
    width: 340px;
    height: 340px;
    top: -90px;
    left: -80px;
    background: radial-gradient(circle at 30% 30%, rgba(234, 88, 12, 0.45), transparent 70%);
    animation: authBlob1 18s ease-in-out infinite;
  }
  .auth-blob-2 {
    width: 300px;
    height: 300px;
    bottom: -100px;
    right: -70px;
    background: radial-gradient(circle at 30% 30%, rgba(124, 58, 237, 0.4), transparent 70%);
    animation: authBlob2 22s ease-in-out infinite;
  }
  .auth-blob-3 {
    width: 260px;
    height: 260px;
    top: 40%;
    right: 12%;
    background: radial-gradient(circle at 30% 30%, rgba(245, 158, 11, 0.35), transparent 70%);
    animation: authBlob3 26s ease-in-out infinite;
  }

  @keyframes authBlob1 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(60px, 40px) scale(1.15); }
  }
  @keyframes authBlob2 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(-50px, -40px) scale(1.1); }
  }
  @keyframes authBlob3 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(-40px, 50px) scale(1.2); }
  }

  /* Shopping emojis falling and gently swaying */
  .auth-float {
    position: absolute;
    top: -60px;
    line-height: 1;
    opacity: 0;
    filter: drop-shadow(0 6px 10px rgba(124, 45, 18, 0.18));
    animation-name: authFall;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    will-change: transform, opacity;
  }

  @keyframes authFall {
    0% {
      transform: translateY(0) translateX(0) rotate(0deg);
      opacity: 0;
    }
    10% { opacity: 0.85; }
    50% { transform: translateY(55vh) translateX(24px) rotate(180deg); }
    90% { opacity: 0.85; }
    100% {
      transform: translateY(108vh) translateX(-16px) rotate(360deg);
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .auth-blob,
    .auth-float {
      animation: none !important;
    }
    .auth-float {
      display: none;
    }
  }
`;

export default AuthBackground;
